import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { readEdgeDetail } from "@/lib/edgeError";
import {
  launchFacebookPageLogin,
  type FacebookLoginConfig,
} from "@/lib/facebookPageLogin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MessengerIcon } from "./BrandIcons";

// Facebook Messenger connection card.
//
// The owner continues with Facebook (Login for Business), picks the Page their
// customers message, and facebook-messenger-onboard exchanges the code for a
// Page token and subscribes the Page to Dibs. When the owner shared several
// Pages, the function returns them with a short-lived sealed selection token
// and the owner chooses one here.

const FUNCTION_NAME = "facebook-messenger-onboard";

interface MessengerState {
  connected: boolean;
  status: string;
  page_id: string | null;
  page_name: string | null;
  connected_at: string | null;
  comments_ready?: boolean;
  facebook_login: FacebookLoginConfig | null;
}

interface PageSelection {
  token: string;
  pages: { id: string; name: string }[];
}

interface ConnectResponse {
  needs_page_selection?: boolean;
  selection_token?: string;
  pages?: { id: string; name: string }[];
  page_name?: string;
}

export default function FacebookMessengerConnectionCard({ className }: { className?: string }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [setupOpen, setSetupOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [busy, setBusy] = useState<"connect" | "select" | "disconnect" | null>(null);
  const [selection, setSelection] = useState<PageSelection | null>(null);
  const [selectedPageId, setSelectedPageId] = useState("");

  const { data: state, isLoading } = useQuery({
    queryKey: ["facebookMessengerConnection", user?.id],
    enabled: !!user,
    staleTime: 15_000,
    queryFn: async (): Promise<MessengerState> => {
      const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, {
        method: "GET",
      });
      if (error) throw error;
      return data as MessengerState;
    },
  });

  const connected = state?.connected === true;
  const loginConfig = state?.facebook_login ?? null;
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["facebookMessengerConnection", user?.id] });

  const openSetup = () => {
    setSelection(null);
    setSelectedPageId("");
    setSetupOpen(true);
  };

  const finishConnected = async (pageName?: string) => {
    toast.success(
      pageName
        ? `${pageName} connected. Messenger chats will now appear in Dibs.`
        : "Facebook Messenger connected.",
    );
    // Leave the Page list in place while the dialog fades out; openSetup
    // clears it for the next time.
    setSetupOpen(false);
    await refresh();
  };

  const continueWithFacebook = async () => {
    if (!loginConfig) return;
    setBusy("connect");
    try {
      const { code, redirectUri } = await launchFacebookPageLogin(loginConfig);
      const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, {
        body: { action: "connect_oauth", code, redirectUri },
      });
      if (error) throw error;
      const result = data as ConnectResponse;
      if (result.needs_page_selection && result.selection_token && result.pages?.length) {
        setSelection({ token: result.selection_token, pages: result.pages });
        setSelectedPageId(result.pages[0].id);
        return;
      }
      await finishConnected(result.page_name);
    } catch (error) {
      console.error("Facebook Messenger connect failed:", error);
      toast.error(await readEdgeDetail(error, "Couldn't connect Facebook Messenger."));
    } finally {
      setBusy(null);
    }
  };

  const connectSelectedPage = async () => {
    if (!selection || !selectedPageId) return;
    setBusy("select");
    try {
      const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, {
        body: {
          action: "select_page",
          selectionToken: selection.token,
          pageId: selectedPageId,
        },
      });
      if (error) throw error;
      await finishConnected((data as ConnectResponse).page_name);
    } catch (error) {
      console.error("Facebook Page selection failed:", error);
      toast.error(await readEdgeDetail(error, "Couldn't connect that Page."));
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async () => {
    setBusy("disconnect");
    try {
      const { error } = await supabase.functions.invoke(FUNCTION_NAME, {
        body: { action: "disconnect" },
      });
      if (error) throw error;
      toast.success("Facebook Messenger disconnected.");
      setDisconnectOpen(false);
      setSetupOpen(false);
      await refresh();
    } catch (error) {
      toast.error(await readEdgeDetail(error, "Couldn't disconnect Facebook Messenger."));
    } finally {
      setBusy(null);
    }
  };

  const dialogTitle = selection
    ? "Choose your Page"
    : connected
      ? "Facebook Messenger connection"
      : "Connect Facebook Messenger";
  const dialogDescription = selection
    ? "You shared more than one Page. Pick the one your customers message."
    : connected
      ? "New Messenger chats on this Page appear in your inbox, and your AI replies to them."
      : "Sign in with Facebook and choose the Page your customers message. You need to be an admin of that Page.";

  return (
    <>
      <div className={`flex items-center justify-between gap-4 rounded-xl border bg-card p-4 shadow-sm ${className ?? ""}`}>
        <div className="flex min-w-0 items-center gap-4">
          <div
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: "rgba(0, 132, 255, 0.10)" }}
          >
            <MessengerIcon className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">Facebook Messenger</h3>
              {connected && (
                <Badge className="border-transparent bg-green-600 text-white hover:bg-green-600">
                  Connected
                </Badge>
              )}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {connected && state?.page_name
                ? `Replying to messages on ${state.page_name}.`
                : "Reply to customers who message your Facebook Page."}
            </p>
          </div>
        </div>

        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <div className="flex shrink-0 items-center gap-3">
            <Switch
              aria-label={connected ? "Disconnect Facebook Messenger" : "Connect Facebook Messenger"}
              checked={connected}
              disabled={busy !== null}
              onCheckedChange={(checked) =>
                checked ? openSetup() : setDisconnectOpen(true)}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  aria-label="Facebook Messenger connection options"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                >
                  <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {connected && (
                  <>
                    <DropdownMenuLabel className="font-normal text-muted-foreground">
                      {state?.page_name ? `Connected to ${state.page_name}` : "Facebook Page connected"}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={openSetup}>
                  {connected ? "Manage connection" : "Connect Facebook Messenger"}
                </DropdownMenuItem>
                {connected && (
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => setDisconnectOpen(true)}
                  >
                    Disconnect
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <Dialog
        open={setupOpen}
        onOpenChange={(open) => {
          if (!busy) setSetupOpen(open);
        }}
      >
        <DialogContent className="admin-modal-content">
          <DialogHeader className="admin-modal-header">
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>{dialogDescription}</DialogDescription>
          </DialogHeader>

          {selection ? (
            <RadioGroup
              value={selectedPageId}
              onValueChange={setSelectedPageId}
              className="gap-2"
            >
              {selection.pages.map((page) => (
                <Label
                  key={page.id}
                  htmlFor={`messenger-page-${page.id}`}
                  className="flex cursor-pointer items-center gap-3 rounded-lg border bg-card px-3 py-2.5 font-normal hover:bg-secondary"
                >
                  <RadioGroupItem id={`messenger-page-${page.id}`} value={page.id} />
                  <span className="min-w-0 truncate text-sm">{page.name}</span>
                </Label>
              ))}
            </RadioGroup>
          ) : connected ? (
            <div className="space-y-1 text-sm">
              <p>
                <span className="text-muted-foreground">Page: </span>
                {state?.page_name ?? "Connected Page"}
              </p>
              {state?.page_id && (
                <p className="font-mono text-xs text-muted-foreground">ID: {state.page_id}</p>
              )}
              {state?.connected_at && (
                <p className="text-xs text-muted-foreground">
                  Connected {new Date(state.connected_at).toLocaleDateString()}.
                </p>
              )}
            </div>
          ) : !loginConfig ? (
            <p className="text-sm text-muted-foreground">
              Facebook sign-in isn't switched on for Dibs yet. Contact Dibs support and
              we'll connect your Page for you.
            </p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="standard-action-button admin-modal-secondary-button neutral-action-button"
              disabled={busy !== null}
              onClick={() => setSetupOpen(false)}
            >
              {selection ? "Cancel" : "Close"}
            </Button>
            {selection ? (
              <Button
                size="sm"
                className="standard-action-button"
                disabled={busy !== null || !selectedPageId}
                onClick={connectSelectedPage}
              >
                {busy === "select" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Connect Page
              </Button>
            ) : loginConfig ? (
              <Button
                size="sm"
                className="standard-action-button"
                disabled={busy !== null}
                onClick={continueWithFacebook}
              >
                {busy === "connect" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {connected ? "Change Page" : "Continue with Facebook"}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Facebook Messenger?</AlertDialogTitle>
            <AlertDialogDescription>
              Dibs will stop replying to messages on your Page. Existing conversations
              stay in your inbox and you can reconnect any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy === "disconnect"}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy === "disconnect"}
              onClick={(event) => {
                event.preventDefault();
                disconnect();
              }}
            >
              {busy === "disconnect" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
